import { forwardRef, useRef, useState, type ComponentPropsWithoutRef } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Check, ChevronRight, LogOut, Settings, UserRound, X } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { useUser } from '../../hooks/useUser'
import { useAccountTheme } from '../../hooks/useAccountTheme'
import { withMountBase } from '../../lib/mountBase'
import { cn } from '../../lib/utils'
import { UserAvatar } from '../UserAvatar'
import { THEME_OPTIONS, type Theme } from '../../lib/accountTheme'

type TriggerKind = 'expanded' | 'avatar'

interface AccountMenuProps {
  trigger: TriggerKind
  mobileSheet?: boolean
  avatarClassName?: string
  onNavigate?: () => void
  className?: string
}

function AccountIdentity() {
  const user = useUser()
  return (
    <div className="flex min-w-0 items-center gap-3">
      <UserAvatar
        src={user.avatarUrl}
        name={user.displayName || user.name}
        email={user.email}
        className="h-10 w-10 shrink-0"
        fallbackClassName="text-sm"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-semibold">{user.displayName || user.name}</p>
          {user.isAdmin && <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-medium text-amber-500">Admin</span>}
        </div>
        <p className="truncate text-xs text-muted-foreground">{user.email}</p>
      </div>
    </div>
  )
}

function TriggerContent({ trigger, avatarClassName }: { trigger: TriggerKind; avatarClassName?: string }) {
  const user = useUser()
  const active = useLocation().pathname.startsWith('/account')
  if (trigger === 'avatar') {
    return (
      <span className={cn(
        'relative inline-flex min-h-11 min-w-11 items-center justify-center rounded-full transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        active && 'bg-primary/10 ring-1 ring-primary/30',
      )}>
        <UserAvatar
          src={user.avatarUrl}
          name={user.displayName || user.name}
          email={user.email}
          className={avatarClassName || 'h-8 w-8'}
          fallbackClassName="text-sm"
        />
        {user.isAdmin && <span className="absolute right-1 top-1 h-3 w-3 rounded-full border-2 border-card bg-amber-500" aria-hidden="true" />}
      </span>
    )
  }

  return (
    <span className={cn(
      'flex min-h-11 w-full items-center gap-3 rounded-lg p-2 text-left transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
      active && 'bg-primary/10',
    )}>
      <UserAvatar src={user.avatarUrl} name={user.displayName || user.name} email={user.email} className="h-8 w-8 shrink-0" fallbackClassName="text-sm" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{user.displayName || user.name}</span>
        <span className="block truncate text-xs text-muted-foreground">{user.email}</span>
      </span>
      <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
    </span>
  )
}

type AccountMenuTriggerProps = Pick<AccountMenuProps, 'trigger' | 'avatarClassName' | 'className'>
  & Omit<ComponentPropsWithoutRef<'button'>, 'children'>

const AccountMenuTrigger = forwardRef<HTMLButtonElement, AccountMenuTriggerProps>(function AccountMenuTrigger(
  { trigger, avatarClassName, className, ...buttonProps },
  ref,
) {
  return (
    <button
      {...buttonProps}
      ref={ref}
      type="button"
      aria-label="Open account menu"
      className={cn('block rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary', trigger === 'expanded' && 'w-full', className)}
    >
      <TriggerContent trigger={trigger} avatarClassName={avatarClassName} />
    </button>
  )
})

function MobileAccountSheet({ trigger, avatarClassName, onNavigate, className }: AccountMenuProps) {
  const [open, setOpen] = useState(false)
  const navigationInProgress = useRef(false)
  const { theme, chooseTheme } = useAccountTheme()

  function finishNavigation() {
    navigationInProgress.current = true
    setOpen(false)
    onNavigate?.()
  }

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <AccountMenuTrigger trigger={trigger} avatarClassName={avatarClassName} className={className} />
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          aria-describedby={undefined}
          onCloseAutoFocus={(event) => {
            if (navigationInProgress.current) {
              event.preventDefault()
              navigationInProgress.current = false
            }
          }}
          className="fixed inset-x-0 bottom-0 z-50 max-h-[min(80dvh,640px)] overflow-y-auto rounded-t-2xl border border-b-0 border-border bg-card px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 shadow-2xl outline-none"
        >
          <div className="mb-4 flex items-center justify-between gap-3">
            <Dialog.Title className="text-lg font-semibold">Account</Dialog.Title>
            <Dialog.Close asChild>
              <button type="button" aria-label="Close account menu" className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </Dialog.Close>
          </div>
          <div className="mb-3 rounded-xl bg-secondary/40 p-3"><AccountIdentity /></div>
          <nav aria-label="Account menu" className="space-y-1">
            <Link to="/account/profile" onClick={finishNavigation} className="flex min-h-12 items-center gap-3 rounded-lg px-3 text-sm font-medium hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
              <UserRound className="h-4 w-4" aria-hidden="true" />View profile
            </Link>
            <Link to="/account/appearance" onClick={finishNavigation} className="flex min-h-12 items-center gap-3 rounded-lg px-3 text-sm font-medium hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
              <Settings className="h-4 w-4" aria-hidden="true" />Account settings
            </Link>
          </nav>
          <div className="my-3 border-t border-border pt-3">
            <p className="mb-2 px-3 text-xs font-medium text-muted-foreground">Theme</p>
            <div className="grid grid-cols-3 gap-2">
              {THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
                <button
                  type="button"
                  key={value}
                  onClick={() => void chooseTheme(value).catch(() => undefined)}
                  className={cn('flex min-h-12 items-center justify-center gap-2 rounded-lg border px-2 text-sm', theme === value ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-secondary')}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />{label}
                </button>
              ))}
            </div>
          </div>
          <a href={withMountBase('/auth/logout')} className="flex min-h-12 items-center gap-3 rounded-lg border-t border-border px-3 text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground">
            <LogOut className="h-4 w-4" aria-hidden="true" />Sign out
          </a>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function DesktopAccountMenu({ trigger, avatarClassName, onNavigate, className }: AccountMenuProps) {
  const { theme, chooseTheme } = useAccountTheme()
  const navigationInProgress = useRef(false)

  function finishNavigation() {
    navigationInProgress.current = true
    onNavigate?.()
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <AccountMenuTrigger trigger={trigger} avatarClassName={avatarClassName} className={className} />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side="right"
          align="end"
          sideOffset={8}
          collisionPadding={12}
          onCloseAutoFocus={(event) => {
            if (navigationInProgress.current) {
              event.preventDefault()
              navigationInProgress.current = false
            }
          }}
          className="z-50 w-72 rounded-xl border border-border bg-card p-2 shadow-2xl outline-none"
        >
          <DropdownMenu.Label className="p-2"><AccountIdentity /></DropdownMenu.Label>
          <DropdownMenu.Separator className="my-2 h-px bg-border" />
          <DropdownMenu.Item asChild>
            <Link to="/account/profile" onClick={finishNavigation} className="flex min-h-10 cursor-pointer items-center gap-3 rounded-lg px-3 text-sm outline-none focus:bg-secondary">
              <UserRound className="h-4 w-4" aria-hidden="true" />View profile
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <Link to="/account/appearance" onClick={finishNavigation} className="flex min-h-10 cursor-pointer items-center gap-3 rounded-lg px-3 text-sm outline-none focus:bg-secondary">
              <Settings className="h-4 w-4" aria-hidden="true" />Account settings
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="my-2 h-px bg-border" />
          <DropdownMenu.Label className="px-3 pb-1 text-xs font-medium text-muted-foreground">Theme</DropdownMenu.Label>
          <DropdownMenu.RadioGroup value={theme} onValueChange={(value) => void chooseTheme(value as Theme).catch(() => undefined)}>
            {THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
              <DropdownMenu.RadioItem key={value} value={value} className="flex min-h-10 cursor-pointer items-center gap-3 rounded-lg px-3 text-sm outline-none focus:bg-secondary">
                <Icon className="h-4 w-4" aria-hidden="true" />{label}
                <DropdownMenu.ItemIndicator className="ml-auto"><Check className="h-4 w-4" aria-hidden="true" /></DropdownMenu.ItemIndicator>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
          <DropdownMenu.Separator className="my-2 h-px bg-border" />
          <DropdownMenu.Item asChild>
            <a href={withMountBase('/auth/logout')} className="flex min-h-10 cursor-pointer items-center gap-3 rounded-lg px-3 text-sm text-muted-foreground outline-none focus:bg-secondary focus:text-foreground">
              <LogOut className="h-4 w-4" aria-hidden="true" />Sign out
            </a>
          </DropdownMenu.Item>
          <DropdownMenu.Arrow className="fill-card" />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

export function AccountMenu(props: AccountMenuProps) {
  return props.mobileSheet ? <MobileAccountSheet {...props} /> : <DesktopAccountMenu {...props} />
}
